import { Spinner } from '@tempo-io/sui-spinner';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import TempoLogo from '@/assets/tempo-logo.svg';
import { NotFoundMessage } from './NotFoundMessage';
import { PasswordPromptModal } from './PasswordPromptModal';
import { PublishedTimeline } from './PublishedTimeline';
import { PublishedViewAuthProvider, usePublishedViewAuth } from './PublishedViewAuthContext';
import { usePublishedTimeline } from './usePublishedTimeline';

export const publishedTimelineSearchSchema = z.object({
  embed: z.union([z.string(), z.number()]).optional(),
  export: z.union([z.string(), z.number()]).optional(),
  title: z.union([z.string(), z.number()]).optional(),
  branding: z.union([z.string(), z.number()]).optional(),
  date: z.union([z.string(), z.number()]).optional(),
  legend: z.union([z.string(), z.number()]).optional(),
  vision: z.string().optional(),
  itemId: z.coerce.number().optional(),
});

export type PublishedTimelineSearchParams = z.infer<typeof publishedTimelineSearchSchema>;

export interface PublishedTimelinePageProps {
  linkId: string;
  searchParams: PublishedTimelineSearchParams;
}

function isQueryParamTruthy(value: string | number | undefined): boolean {
  return value === '1' || value === 1;
}

interface ResolvedViewMode {
  readonly kind: 'default' | 'export' | 'embed';
  readonly allowsQueryOverrides: boolean;
  readonly overflowClass: 'overflow-hidden' | '';
}

function resolveViewMode(searchParams: PublishedTimelineSearchParams): ResolvedViewMode {
  if (isQueryParamTruthy(searchParams.export)) {
    return { kind: 'export', allowsQueryOverrides: true, overflowClass: '' };
  }
  if (isQueryParamTruthy(searchParams.embed)) {
    return { kind: 'embed', allowsQueryOverrides: true, overflowClass: 'overflow-hidden' };
  }
  return { kind: 'default', allowsQueryOverrides: false, overflowClass: 'overflow-hidden' };
}

function getBooleanQueryParam(value: string | number | undefined, defaultValue: boolean): boolean {
  if (value === undefined) return defaultValue;
  return isQueryParamTruthy(value);
}

export function PublishedTimelinePage(props: PublishedTimelinePageProps) {
  return (
    <PublishedViewAuthProvider>
      <PublishedTimelinePageContent {...props} />
    </PublishedViewAuthProvider>
  );
}

function PublishedTimelinePageContent({ linkId, searchParams }: PublishedTimelinePageProps) {
  const { t } = useTranslation();
  const { submit } = usePublishedViewAuth();
  const result = usePublishedTimeline(linkId);

  if (result.status === 'loading') {
    return (
      <div className="flex w-full min-h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <h1 className="text-2xl font-bold text-text-neutral-primary">
            {t(($) => $.views.published.loading)}
          </h1>
          <Spinner size={32} />
        </div>
      </div>
    );
  }
  if (result.status === 'error') {
    return (
      <div
        className="flex items-center justify-center h-screen text-xl text-text-error"
        role="alert"
      >
        {t(($) => $.views.published.errorLoading, { message: result.error.message })}
      </div>
    );
  }
  if (result.status === 'password_required') {
    return <PasswordPromptModal authStatus="password_required" onSubmit={submit} />;
  }
  if (result.status === 'password_invalid') {
    return <PasswordPromptModal authStatus="password_invalid" onSubmit={submit} />;
  }
  if (result.status === 'not_found') {
    return <NotFoundMessage />;
  }

  const { metadata } = result.data;

  const { allowsQueryOverrides, overflowClass } = resolveViewMode(searchParams);

  const showTitle = getBooleanQueryParam(
    allowsQueryOverrides ? searchParams.title : undefined,
    metadata.preferences?.showTitle ?? true,
  );
  const showVisionStatement =
    allowsQueryOverrides && searchParams.vision !== undefined
      ? true
      : (metadata.preferences?.showVisionStatement ?? false);
  const showPublishDate = getBooleanQueryParam(
    allowsQueryOverrides ? searchParams.date : undefined,
    metadata.preferences?.showPublishDate ?? true,
  );
  const showTempoBranding = getBooleanQueryParam(
    allowsQueryOverrides ? searchParams.branding : undefined,
    metadata.preferences?.showTempoBranding ?? true,
  );
  const showLegend = getBooleanQueryParam(
    allowsQueryOverrides ? searchParams.legend : undefined,
    metadata.preferences?.showLegend ?? true,
  );
  const visionStatement =
    allowsQueryOverrides && searchParams.vision !== undefined
      ? searchParams.vision
      : (metadata.visionStatement ?? '');

  const showHeader = showTitle || (showVisionStatement && !!visionStatement);

  const publishDate = new Date(metadata.publishedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const rootContainerClass = 'h-screen w-screen bg-bg-surface-base flex flex-col';
  const timelineContainerClass = `flex-1 ${overflowClass}`.trim();

  return (
    <div className={rootContainerClass}>
      {showHeader && (
        <div className="flex-none px-8 pt-6 pb-4 border-b border-border-neutral">
          {showTitle && (
            <h1 className="text-2xl font-bold text-text-neutral-primary mb-2">{metadata.title}</h1>
          )}
          {showVisionStatement && visionStatement && (
            <div className="max-h-32 overflow-y-auto">
              <p className="text-sm text-text-neutral-secondary whitespace-pre-wrap">
                {visionStatement}
              </p>
            </div>
          )}
        </div>
      )}

      <div className={timelineContainerClass}>
        <PublishedTimeline data={result.data} searchParams={searchParams} showLegend={showLegend} />
      </div>

      {(showPublishDate || showTempoBranding) && (
        <div className="flex-none py-4 w-full border-t border-border-neutral">
          <div className="flex items-center justify-between px-8">
            <div className="text-sm text-text-neutral-secondary">
              {showPublishDate && (
                <span>{t(($) => $.views.published.publishedOn, { date: publishDate })}</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {showTempoBranding && (
                <div className="flex items-center gap-2">
                  <img src={TempoLogo} alt="Tempo" className="h-6 w-6" />
                  <span className="text-sm font-semibold text-text-neutral-primary">Tempo SPM</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
